import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  RowComponent,
  SpinnerModule
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { AccountService } from 'app';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-delete-account-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    FormControlDirective,
    AlertComponent,
    IconDirective,
    SpinnerModule,
    TranslatePipe
  ],
  templateUrl: './delete-account-page.component.html',
  styleUrls: ['./delete-account-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeleteAccountPageComponent implements OnDestroy {
  private accountService = inject(AccountService);
  private router = inject(Router);
  private i18n = inject(I18nService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  step = signal<'initial' | 'awaiting_code' | 'success'>('initial');
  loading = signal(false);
  error = signal<string | null>(null);
  success = signal<string | null>(null);

  confirmationCode = signal<string>('');
  codeExpiresAt = signal<Date | null>(null);

  // Computed timer display
  timeRemaining = computed(() => {
    const expires = this.codeExpiresAt();
    if (!expires) return '';

    const now = Date.now();
    const diff = expires.getTime() - now;

    if (diff <= 0) return 'Expired';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  isCodeExpired = computed(() => {
    const expires = this.codeExpiresAt();
    return expires ? Date.now() > expires.getTime() : false;
  });

  // ────────────────────────────────────────────────
  // Timer effect (updates every second)
  // ────────────────────────────────────────────────
  private timerInterval: any;

  constructor() {
    // Start timer when code is requested
    effect(() => {
      if (this.step() === 'awaiting_code' && this.codeExpiresAt()) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    });
  }

  private startTimer(): void {
    this.stopTimer(); // prevent duplicates
    this.timerInterval = setInterval(() => {
      // Force recompute timeRemaining
      this.codeExpiresAt.update(v => v); // dummy update to trigger computed
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  // ────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────
  requestDeletionCode(): void {
    this.loading.set(true);
    this.error.set(null);
    this.success.set(null);

    this.accountService.requestDeletion()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.loading.set(false);
            this.step.set('awaiting_code');
            this.codeExpiresAt.set(new Date(Date.now() + (response.expires_in_minutes * 60 * 1000)));
            this.success.set(this.translate.translate('delete_account.code_sent'));
          },
          error: err => {
            this.loading.set(false);
            this.error.set(err.error?.error || this.translate.translate('delete_account.error_request'));
          }
        });
  }

  confirmDeletion(): void {
    const code = this.confirmationCode();
    if (!code || code.length !== 6) {
      this.error.set(this.translate.translate('delete_account.error_invalid_code'));
      return;
    }

    if (this.isCodeExpired()) {
      this.error.set(this.translate.translate('delete_account.error_expired_code'));
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.accountService.confirmDeletion(code)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.loading.set(false);
            this.step.set('success');
            this.success.set(response.message || this.translate.translate('delete_account.success_message'));

            // Redirect after delay
            setTimeout(() => {
              this.authService.logout();
              const redirectUrl = response.goto || '/login';
              window.location.href = redirectUrl;
            }, 3000);
          },
          error: err => {
            this.loading.set(false);
            this.error.set(err.error?.error || this.translate.translate('delete_account.error_confirm'));
          }
        });
  }

  cancelDeletion(): void {
    this.loading.set(true);
    this.error.set(null);

    this.accountService.cancelDeletion()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.resetState();
          },
          error: () => {
            this.loading.set(false);
            this.resetState(); // reset locally even on error
          }
        });
  }

  private resetState(): void {
    this.step.set('initial');
    this.confirmationCode.set('');
    this.codeExpiresAt.set(null);
    this.success.set(null);
  }

  navigateBack(): void {
    const lang = this.i18n.getLanguage();
    this.router.navigate(['/', lang, 'profile']);
  }
}